import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userAtom, tokenAtom } from '../atoms/authAtoms';
import { logout } from '../utils/auth';
import { ROUTES } from '../constants';
import { AnimatePresence, motion } from 'framer-motion';
import { isAuthenticated, getCurrentUser, logoutUser, isAdmin as checkIfUserIsAdmin } from '../login/controller/login-controller';
import CartIcon from './CartIcon';
import spinService from '../services/spinService';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useAtom(userAtom);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCouponsModalOpen, setIsCouponsModalOpen] = useState(false);
  const [myCoupons, setMyCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [activeCouponCount, setActiveCouponCount] = useState(0);
  const userMenuRef = useRef(null);
  const [recentSearches] = useState(['Sustainable products', 'Premium bags', 'Eco gifts']);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults] = useState([
    { id: 1, name: 'Eco-friendly Water Bottle', category: 'Accessories' },
    { id: 2, name: 'Bamboo Toothbrush Set', category: 'Health' },
    { id: 3, name: 'Organic Cotton T-shirt', category: 'Clothing' },
  ]);
  const searchInputRef = useRef(null);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [activeMegaMenuSection, setActiveMegaMenuSection] = useState('new');
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useAtom(tokenAtom);

  useEffect(() => {
    setIsLoggedIn(!!token);
  }, [token]);
  // Handle click outside user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Focus search input when search modal opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const checkAuthStatus = () => {
      const authenticated = isAuthenticated();
      setIsLoggedIn(authenticated);

      if (authenticated) {
        const userData = getCurrentUser();
        setUser(userData);
      }
    };

    checkAuthStatus();

    // Listen for storage events to update navbar when auth changes in another tab
    window.addEventListener('storage', checkAuthStatus);

    return () => {
      window.removeEventListener('storage', checkAuthStatus);
    };
  }, [setUser]);

  const openCoupons = async () => {
    setIsUserMenuOpen(false);
    setIsCouponsModalOpen(true);
    setCouponsLoading(true);
    try {
      const data = await spinService.getMyCoupons();
      const coupons = Array.isArray(data) ? data : [];
      setMyCoupons(coupons);
      // Update active count (non-used, non-expired)
      const now = new Date();
      setActiveCouponCount(coupons.filter(c => !c.isUsed && new Date(c.expirationDate) >= now).length);
    } catch {
      setMyCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  };

  // Fetch active coupon count for client users on mount
  useEffect(() => {
    if (isLoggedIn && user?.type === 'Client') {
      spinService.getMyCoupons().then(data => {
        const coupons = Array.isArray(data) ? data : [];
        const now = new Date();
        setActiveCouponCount(coupons.filter(c => !c.isUsed && new Date(c.expirationDate) >= now).length);
      }).catch(() => setActiveCouponCount(0));
    } else {
      setActiveCouponCount(0);
    }
  }, [isLoggedIn, user?.type]);

  const handleLogout = () => {
    logoutUser();
    setIsLoggedIn(false);
    setUser(null);
    setIsUserMenuOpen(false);
    navigate(ROUTES.login);
  };

  const isAdminUser = isLoggedIn && checkIfUserIsAdmin();

  // Escape key handler for modals
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (isSearchOpen) setIsSearchOpen(false);
        if (isUserMenuOpen) setIsUserMenuOpen(false);
        if (isMegaMenuOpen) setIsMegaMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isSearchOpen, isUserMenuOpen, isMegaMenuOpen]);

  // Animation variants
  const fadeInAnimation = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };

  const searchAnimation = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } }
  };

  // Add this function to get the user avatar URL
  const getUserAvatar = () => {
    if (user && user.image) {
      return user.image;
    }
    // Generate a default avatar with user's initials if available
    if (user && (user.firstName || user.lastName)) {
      const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`;
      return `https://ui-avatars.com/api/?name=${initials}&background=174540&color=fff&size=128`;
    }
    // Fallback to default image
    return "https://randomuser.me/api/portraits/men/40.jpg";
  };

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${isScrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-lg py-2'
            : 'bg-white/90 shadow-sm py-4'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <Link to={ROUTES.home} className="group flex items-center space-x-2">
                  <div className="relative">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-[#174540] to-[#83c5be] rounded-full blur opacity-60 group-hover:opacity-80 transition duration-300"></div>
                    <div className="relative w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-sm overflow-hidden">
                      <img
                        src="/logo.jpg"
                        alt="Brand Logo"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>
                  </div>
                  <span className="text-[#174540] font-bold text-xl tracking-tight">Tendresse</span>
                </Link>
              </div>

              <div className="hidden md:ml-10 md:flex md:items-center md:space-x-8">
                <Link
                  to={ROUTES.home}
                  className={`relative overflow-hidden group py-5 ${location.pathname === ROUTES.home
                      ? 'text-[#174540] font-medium'
                      : 'text-gray-500 hover:text-[#174540]'
                    }`}
                >
                  <span className="relative z-10 text-sm tracking-wide uppercase transition-colors duration-200">Accueil</span>
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#174540] to-[#83c5be] transform origin-left transition-transform duration-300 ease-out ${location.pathname === ROUTES.home ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}></span>
                </Link>

                {isLoggedIn && user?.type !== 'Client' && (
                  <Link
                    to={ROUTES.admin}
                    className="relative overflow-hidden group py-5 text-gray-500 hover:text-[#174540]"
                  >
                    <span className="relative z-10 text-sm tracking-wide uppercase transition-colors duration-200">Administration</span>
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#174540] to-[#83c5be] transform origin-left transition-transform duration-300 ease-out scale-x-0 group-hover:scale-x-100"></span>
                  </Link>
                )}
              </div>
            </div>

            <div className="hidden md:flex md:items-center md:space-x-5">
              <CartIcon />
              {isLoggedIn ? (
                <div className="flex space-x-3 items-center">
                  {/* Cart Icon (add this) */}
                  
                  {/* Existing profile dropdown */}
                  <div className="relative ml-3" ref={userMenuRef}>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center gap-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#83c5be] rounded-full"
                    >
                      <div className="h-8 w-8 rounded-full overflow-hidden border-2 border-gray-200">
                        <img 
                          src={getUserAvatar()} 
                          alt={`${user?.firstName || 'User'}'s profile`}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            // Fallback if image fails to load
                            e.target.src = "https://ui-avatars.com/api/?name=User&background=174540&color=fff";
                          }}
                        />
                      </div>
                      <span className="text-gray-700 text-sm font-medium hidden lg:block">
                        {user?.fullName?.split('@')[0] || 'User'}
                      </span>
                      <svg className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          variants={fadeInAnimation}
                          className="absolute right-0 w-60 mt-3 origin-top-right bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50"
                        >
                          <div className="py-3 px-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white rounded-t-lg">
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Connecté en tant que</p>
                            <p className="text-sm font-medium text-gray-900 truncate mt-0.5">{user?.fullName || 'Utilisateur'}</p>
                          </div>

                          <div className="py-2">
                            {user?.type === 'Client' && (
                              <button
                                onClick={openCoupons}
                                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 transition-colors duration-150"
                              >
                                <svg className="h-4 w-4 mr-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                </svg>
                                <span>Mes Coupons</span>
                              </button>
                            )}
                            {/* <a href="#orders" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150">
                              <svg className="h-4 w-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                              </svg>
                              <span>Commandes & Retours</span>
                            </a>
                            <a href="#saved" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150">
                              <svg className="h-4 w-4 mr-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                              </svg>
                              <span>Articles Sauvegardés</span>
                            </a> */}
                          </div>

                          <div className="border-t border-gray-100 pt-2 pb-1">
                            <button
                              onClick={handleLogout}
                              className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                            >
                              <svg className="h-4 w-4 mr-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              <span>Déconnexion</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link
                    to={ROUTES.login}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-[#174540] bg-white border border-[#174540]/20 rounded-lg hover:bg-gray-50 hover:border-[#174540]/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#83c5be] transition-all duration-200 shadow-sm"
                  >
                    Se connecter
                  </Link>
                  {/* <Link
                    to={ROUTES.signup}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#174540] to-[#1d6b63] border border-transparent rounded-lg hover:from-[#123c39] hover:to-[#195f58] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#83c5be] shadow-md transition-all duration-200 relative overflow-hidden group"
                  >
                    <span className="absolute top-0 left-0 w-full h-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></span>
                    <span className="relative">S'inscrire</span>
                  </Link> */}
                </div>
              )}
            </div>

            <div className="flex items-center md:hidden">
                <div className="flex space-x-2">
                  {/* Replace this hardcoded cart with CartIcon component */}
                  <CartIcon />

                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className="p-2 rounded-full text-gray-500 hover:text-[#174540] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#83c5be] transition-colors"
                    aria-label="Search"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                type="button"
                className="ml-2 inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-[#174540] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#83c5be]"
                aria-expanded="false"
              >
                <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open menu'}</span>
                {!isMenuOpen ? (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu drawer - Enhanced Professional Design */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[60] lg:hidden"
        >
          {/* Backdrop with blur effect */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          ></motion.div>

          {/* Slide-in menu panel */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative flex flex-col w-[85%] max-w-xs h-full bg-white shadow-2xl overflow-hidden"
          >
            {/* Top section with logo and close button */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <Link to={ROUTES.home} className="group flex items-center space-x-2" onClick={() => setIsMenuOpen(false)}>
                <div className="relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-[#174540] to-[#83c5be] rounded-full blur opacity-60 group-hover:opacity-80 transition duration-300"></div>
                  <div className="relative w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-sm overflow-hidden">
                    <img
                      src="/logo.jpg"
                      alt="Brand Logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <span className="text-[#174540] font-bold text-lg tracking-tight">Tendresse</span>
              </Link>

              <button
                type="button"
                className="rounded-full p-2 flex items-center justify-center text-gray-400 hover:text-[#174540] hover:bg-gray-50 transition-all duration-200 focus:outline-none"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User profile section - Only shown when logged in */}
            {isLoggedIn && (
              <div className="bg-gradient-to-r from-[#174540]/5 to-[#83c5be]/5">
                <div className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#174540] to-[#83c5be] flex items-center justify-center text-white shadow-md">
                      <span className="text-lg font-semibold">{user?.fullName?.charAt(0).toUpperCase() || 'U'}</span>
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-gray-900">{user?.fullName || 'User'}</h3>
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">{user?.email || ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main navigation links */}
            <div className="flex-1 overflow-y-auto py-4 px-3">
              <div className="space-y-1">
                {/* Navigation section heading */}
                <div className="px-3 py-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Navigation</h3>
                </div>

                {/* Navigation links with enhanced visual treatments */}
                <Link
                  to={ROUTES.home}
                  className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${location.pathname === ROUTES.home
                      ? 'bg-gradient-to-r from-[#174540]/10 to-[#83c5be]/10 text-[#174540] font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className={`mr-3 h-5 w-5 ${location.pathname === ROUTES.home ? 'text-[#174540]' : 'text-gray-500'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span className="text-base">Accueil</span>
                  {location.pathname === ROUTES.home && (
                    <span className="ml-auto h-2 w-2 rounded-full bg-[#174540]"></span>
                  )}
                </Link>

                {/* Admin section - Only shown for non-client users */}
                {isLoggedIn && user?.type !== 'Client' && (
                  <Link
                    to={ROUTES.admin}
                    className={`flex items-center px-3 py-3 rounded-lg transition-all duration-200 ${location.pathname.startsWith(ROUTES.admin)
                        ? 'bg-gradient-to-r from-[#174540]/10 to-[#83c5be]/10 text-[#174540] font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className={`mr-3 h-5 w-5 ${location.pathname.startsWith(ROUTES.admin) ? 'text-[#174540]' : 'text-gray-500'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-base">Administration</span>
                    {location.pathname.startsWith(ROUTES.admin) && (
                      <span className="ml-auto h-2 w-2 rounded-full bg-[#174540]"></span>
                    )}
                  </Link>
                )}
              </div>
            </div>

            {/* Bottom actions section */}
            <div className="border-t border-gray-200 px-4 py-4">
              {!isLoggedIn ? (
                <div className="space-y-3">
                  <Link
                    to={ROUTES.login}
                    className="flex justify-center items-center px-4 py-2.5 text-sm font-medium text-[#174540] bg-white border border-[#174540]/30 rounded-lg hover:bg-gray-50 focus:outline-none shadow-sm transition-all duration-200 w-full"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Se connecter
                  </Link>
                  <Link
                    to={ROUTES.signup}
                    className="flex justify-center items-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#174540] to-[#1d6b63] rounded-lg hover:from-[#123c39] hover:to-[#195f58] focus:outline-none shadow-md transition-all duration-200 w-full"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="relative z-10">S'inscrire</span>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {user?.type === 'Client' && (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        openCoupons();
                      }}
                      className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors duration-200"
                    >
                      <svg className="mr-2 h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                      Mes Coupons
                    </button>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
                  >
                    <svg className="mr-2 h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Déconnexion
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Floating Coupon Button — visible when client has active coupons */}
      {isLoggedIn && user?.type === 'Client' && activeCouponCount > 0 && (
        <button
          onClick={openCoupons}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-emerald-600 to-[#174540] rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group"
          title="Mes Coupons"
        >
          <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1 animate-pulse">
            {activeCouponCount}
          </span>
        </button>
      )}

      {/* Coupons Modal */}
      <AnimatePresence>
        {isCouponsModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={(e) => { if (e.target === e.currentTarget) setIsCouponsModalOpen(false); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <h2 className="text-base font-semibold text-gray-900">Mes Coupons</h2>
                </div>
                <button
                  onClick={() => setIsCouponsModalOpen(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="px-5 py-4 max-h-80 overflow-y-auto">
                {couponsLoading ? (
                  <div className="flex justify-center py-8">
                    <svg className="animate-spin h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                ) : myCoupons.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-3">🎰</div>
                    <p className="text-sm text-gray-500">Vous n'avez pas encore de coupons actifs.</p>
                    <p className="text-xs text-gray-400 mt-1">Jouez à la roulette pour en gagner !</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {myCoupons.map((coupon) => (
                      <li key={coupon.id} className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-lg font-bold text-emerald-700">{coupon.pourcentage}% de réduction</span>
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Actif</span>
                        </div>
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-sm font-mono font-bold tracking-widest bg-white border border-dashed border-emerald-300 px-3 py-1 rounded-lg text-gray-800 flex-1 text-center">
                            {coupon.code}
                          </code>
                          <button
                            onClick={() => navigator.clipboard.writeText(coupon.code)}
                            className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded-md transition-colors"
                            title="Copier"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          Expire le {new Date(coupon.expirationDate).toLocaleDateString('fr-FR')}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-400 text-center">Entrez le code dans le champ "Code promo" au moment de commander</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;

